import type { MutableRefObject } from "react"
import { useEffect, useRef } from "react"

import type { ChatAppResponse } from "@/api/models"

type UseChatScrollProps = {
  chatState: {
    messages: [string, ChatAppResponse][]
    streamingAnswer: [string, ChatAppResponse] | null
    isStreaming: boolean
    isLoading: boolean
  }
  chatMessageStreamEnd: MutableRefObject<HTMLDivElement | null>
  /**
   * Monotonic counter that callers bump whenever streamed content grows in a
   * way the hook should react to — used as a belt-and-suspenders trigger for
   * cases where the `streamingAnswer` reference is re-used while its inner
   * `thinking` / `content` grows (e.g. Deep Explorer `deep_planning_chunk`
   * updates that zustand may batch into the same tuple). Defaults to 0 for
   * Normal-chat callers that rely on `streamingAnswer` reference identity.
   */
  streamingVersion?: number
  /**
   * The currently-open chat session's id. Used only to detect "the user
   * switched to a different chat" so the hook can reset its scroll-away
   * tracking, which is otherwise scoped to this hook instance rather than to
   * any one session — see the session-switch reset effect below. Optional:
   * callers that don't pass it simply don't get that reset (no worse than
   * before this prop existed).
   */
  sessionId?: string | null
}

const SCROLL_STICKY_THRESHOLD_PX = 50
// How close to the bottom counts as "back at the bottom" (resume auto-scroll)
// after a move that was not upward, and as "at the bottom" when the scroll
// listener seeds following. Deliberately much tighter than
// SCROLL_STICKY_THRESHOLD_PX, which is for "keep auto-pinning while already
// following along". Upward moves never re-arm, whatever their size: see
// `handleScroll` below.
const SCROLL_RESUME_THRESHOLD_PX = 4
// How far the view may move up beyond what a shrinking scroll range explains
// before `handleScroll` counts it as the user leaving. scrollTop can be
// fractional (browser zoom, HiDPI) while scrollHeight is a whole number, so a
// clamp can overshoot the shrink by a fraction of a pixel. Moves within the
// tolerance add up across events and never re-arm following, so a real move
// up of a pixel or less from the wheel or touch is still caught by those
// listeners.
const SCROLL_UP_TOLERANCE_PX = 1
const CHAT_CONTENT_TEST_ID = "chat-content"

const resolveChatScrollContainer = (startElement: HTMLElement): HTMLElement | null => {
  const chatContentContainer = startElement.closest(`[data-testid="${CHAT_CONTENT_TEST_ID}"]`)
  if (chatContentContainer instanceof HTMLElement) {
    return chatContentContainer
  }
  return null
}

// Always instant: a smooth scroll leaves scrollTop at its start when scrollTo
// returns and passes through positions above the bottom for several frames,
// which the scroll handler cannot tell apart from the user moving.
const scrollContainerToBottom = (
  container: HTMLElement,
  lastScrollRef: { current: ScrollSnapshot }
) => {
  const targetTop = Math.max(container.scrollHeight - container.clientHeight, 0)
  container.scrollTo({ top: targetTop, behavior: "auto" })
  // Record where this scroll left the view, so the scroll handler measures
  // the next move from here. Browsers merge scroll events per frame: if the
  // user jumps up (a citation) before this pin's own event arrives, the one
  // event they deliver must still read as an upward move.
  lastScrollRef.current = takeScrollSnapshot(container)
}

// `range` is how far the container can scroll (scrollHeight - clientHeight).
type ScrollSnapshot = { top: number; range: number }

// `top` is clamped to the range: Safari/iOS elastic overscroll reports
// scrollTop past either end while bouncing, and the bounce back must not read
// as a move.
const takeScrollSnapshot = (container: HTMLElement): ScrollSnapshot => {
  const range = Math.max(container.scrollHeight - container.clientHeight, 0)
  return { top: Math.min(Math.max(container.scrollTop, 0), range), range }
}

const isSnapshotAtBottom = (snapshot: ScrollSnapshot, thresholdPx: number): boolean =>
  snapshot.top >= snapshot.range - thresholdPx

export function useChatScroll({
  chatState,
  chatMessageStreamEnd,
  streamingVersion = 0,
  sessionId,
}: UseChatScrollProps) {
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const isAutoScrollEnabledRef = useRef(true)
  const pendingScrollTimeoutIdRef = useRef<number | null>(null)
  // The scroll position and range as of the previous scroll event or of this
  // hook's own last scroll, whichever came later, so `handleScroll` can tell
  // which way the viewport moved and why. See `scrollContainerToBottom`.
  const lastScrollRef = useRef<ScrollSnapshot>({ top: 0, range: 0 })
  // As of the last "messages changed" effect run: whether the turn in
  // `streamingAnswer` had stopped (finished, stopped or failed, or none at
  // all), and which turn it was. Together they tell a new turn from a
  // restart of the same one; see `isTurnStart` in that effect.
  const lastTurnSettledRef = useRef(true)
  const lastTurnKeyRef = useRef<string | null>(null)
  // Tracks whether a turn was still in flight the last time the "messages
  // changed" effect ran, so it can tell "this turn just completed" (respect
  // the user's scroll-away choice) apart from "new question / session
  // switch" (always pin to bottom) — see that effect below. A turn's
  // completion is two separate store updates, not one: `response_complete`
  // flips `isStreaming` to false first (use-stream-handler.ts's
  // `responseComplete` branch), and only ~80ms later (FINALIZE_SETTLE_MS)
  // does the settle timeout actually move the answer into `messages` and
  // null out `streamingAnswer`. So "was mid-turn" has to stay true across
  // that gap — tracking `isStreaming` alone gets reset by the first update
  // and is already false (and un-protective) by the time the second one,
  // the one that visually matters, arrives.
  const wasMidTurnRef = useRef(false)
  // Tracks the `messages` array length as of the last "messages changed"
  // effect run, so that effect can tell "a new question was appended" (an
  // actual new turn, which should always pin to bottom) apart from "an
  // existing message was patched in place" (the turn already finished and
  // this is a trailing metadata update — the `message_saved` ack fallback
  // merge, or the always-late `followup_questions` frame — which should
  // respect the user's scroll-away choice instead of yanking them back
  // down). Both trailing updates land AFTER `wasMidTurnRef` has already
  // reset to false (the settle update that folds `streamingAnswer` into
  // `messages` is what resets it), so `wasMidTurn` alone can't catch them —
  // see the messages effect below.
  const lastMessageCountRef = useRef(0)
  const lastSessionIdRef = useRef<string | null | undefined>(undefined)

  // A session switch must always start fresh. Without this, scrolling away
  // during an active stream and then switching to a different (idle) chat
  // leaves `isAutoScrollEnabledRef` disabled and `wasMidTurnRef` true —
  // state that belongs to the PREVIOUS session — which makes the messages
  // effect below treat opening the new chat as "a turn I should respect the
  // scroll-away choice for" and skip pinning it to the bottom. Declared
  // first so it resets both refs before any other effect in this commit
  // reads them (effects run in declaration order). No-op for callers that
  // don't pass `sessionId`.
  useEffect(() => {
    if (lastSessionIdRef.current === sessionId) return
    lastSessionIdRef.current = sessionId
    wasMidTurnRef.current = false
    isAutoScrollEnabledRef.current = true
    // Reset to 0 (not the new session's current count) so the messages
    // effect below sees a "count changed" transition on the very next run
    // and force-pins the freshly-opened chat to the bottom, regardless of
    // how many messages it has.
    lastMessageCountRef.current = 0
  }, [sessionId])

  // Hide browser scrollbar while streaming to avoid flicker, but keep scrolling enabled
  useEffect(() => {
    const endElement = chatMessageStreamEnd.current
    if (!endElement) return

    const container = scrollContainerRef.current ?? resolveChatScrollContainer(endElement)
    if (!container) {
      scrollContainerRef.current = null
      return
    }
    scrollContainerRef.current = container

    if (chatState.isStreaming) {
      container.classList.add("hide-scrollbar")
    } else {
      container.classList.remove("hide-scrollbar")
    }
    return () => {
      container.classList.remove("hide-scrollbar")
    }
  }, [chatState.isStreaming, chatMessageStreamEnd])

  useEffect(() => {
    const endElement = chatMessageStreamEnd.current
    if (!endElement) return

    const container = scrollContainerRef.current ?? resolveChatScrollContainer(endElement)
    if (!container) {
      scrollContainerRef.current = null
      return
    }
    scrollContainerRef.current = container
    // An EMPTY conversation has nothing to pin. Scrolling it to the bottom is
    // what shoved the agent greeting's avatar under the workspace header on
    // short viewports (QA 2026-09-13, 1200x655): the empty state is taller
    // than the viewport, so "bottom" means "clip the top".
    if (
      chatState.messages.length === 0 &&
      !chatState.isStreaming &&
      chatState.streamingAnswer === null
    )
      return
    scrollContainerToBottom(container, lastScrollRef)
    // chatState fields beyond isLoading are read as guards only; re-running on
    // their changes belongs to the messages effect below.
  }, [chatState.isLoading, chatMessageStreamEnd])

  useEffect(() => {
    // See `wasMidTurnRef`'s definition for why this checks "was mid-turn"
    // rather than the current `chatState.isStreaming` — the latter is
    // already false (and unprotective) for the `messages` update that
    // actually lands the finished answer. New question / session switch
    // still pin to bottom, since `wasMidTurn` is false in both of those
    // cases (no turn was in flight beforehand).
    //
    // Update the ref unconditionally, before any early return below — if a
    // render where the DOM isn't resolvable yet skipped the update, the ref
    // would read one transition stale on the next run and could wrongly let
    // a later mid-stream `messages` update force-scroll past a user who'd
    // scrolled away.
    const wasMidTurn = wasMidTurnRef.current
    wasMidTurnRef.current = chatState.isStreaming || chatState.streamingAnswer !== null

    // Same "update unconditionally, before any early return" reasoning as
    // `wasMidTurnRef` above applies here too.
    const previousMessageCount = lastMessageCountRef.current
    lastMessageCountRef.current = chatState.messages.length
    // A turn that already finished (`wasMidTurn` false) but whose message
    // count didn't change is a trailing in-place patch to an existing
    // message — the `message_saved` fallback merge or the late
    // `followup_questions` frame — not a new question. Treat it the same as
    // the mid-turn case below: respect the user's scroll-away choice.
    const isTrailingInPlaceUpdate =
      !wasMidTurn && chatState.messages.length === previousMessageCount
    // A new turn starting always follows, even if the reader scrolled away
    // during the previous answer. Most turns are also pinned by the isLoading
    // effect, but voice streaming sets isStreaming without touching
    // isLoading; and the scroll listener seeds following from the real
    // position when it attaches, so without this a whole voice answer would
    // go unfollowed. It is a new turn when nothing was in flight before, or
    // when streaming starts after the previous answer had stopped (Stop and
    // a failed turn keep their partial answer in `streamingAnswer`) or for a
    // different turn. A restart of the same turn mid-answer (Deep
    // Explorer toggling isStreaming) is none of these and keeps the reader's
    // position.
    const answerNow = chatState.streamingAnswer
    const wasTurnSettled = lastTurnSettledRef.current
    const lastTurnKey = lastTurnKeyRef.current
    // `turn_id` is stamped at stream start; question text is the fallback for
    // paths that don't set one, and is not safe on its own (a follow-up can
    // repeat the same words, #2426).
    const turnKey = answerNow ? `${answerNow[1].turn_id ?? ""}\u0000${answerNow[0]}` : null
    lastTurnSettledRef.current =
      !chatState.isStreaming && (answerNow === null || answerNow[1].completed === true)
    lastTurnKeyRef.current = turnKey
    const isTurnStart =
      wasMidTurnRef.current &&
      (!wasMidTurn ||
        (chatState.isStreaming &&
          (wasTurnSettled || (turnKey !== null && turnKey !== lastTurnKey))))
    if (isTurnStart) isAutoScrollEnabledRef.current = true

    // Scroll to bottom when navigating to a new chat
    const endElement = chatMessageStreamEnd.current
    if (!endElement) return

    const container = scrollContainerRef.current ?? resolveChatScrollContainer(endElement)
    if (!container) {
      scrollContainerRef.current = null
      return
    }
    scrollContainerRef.current = container

    if ((wasMidTurn || isTrailingInPlaceUpdate) && !isAutoScrollEnabledRef.current) return
    // Same empty-conversation guard as the isLoading effect above: a freshly
    // opened chat with no messages (the greeting/empty state) must stay at
    // the top, not be pinned to the bottom of an overflowing empty state.
    if (chatState.messages.length === 0 && !wasMidTurnRef.current) return
    scrollContainerToBottom(container, lastScrollRef)
    // chatState.streamingAnswer is read above only to compute wasMidTurnRef and is
    // deliberately NOT a dependency here: adding it would re-run this effect on every
    // streamed chunk (a new tuple per chunk), duplicating the "immediate scroll" effect's
    // job for no benefit. isStreaming/messages already cover every transition this effect
    // needs to react to.
  }, [chatState.messages, chatState.isStreaming, chatMessageStreamEnd])

  // Track whether user is at bottom while streaming (and cancel any pending scroll when they scroll up)
  useEffect(() => {
    const clearPendingScroll = () => {
      if (pendingScrollTimeoutIdRef.current !== null) {
        window.clearTimeout(pendingScrollTimeoutIdRef.current)
        pendingScrollTimeoutIdRef.current = null
      }
    }

    if (!chatState.isStreaming) {
      clearPendingScroll()
      return
    }

    const endElement = chatMessageStreamEnd.current
    if (!endElement) return

    const container = resolveChatScrollContainer(endElement)
    if (!container) {
      scrollContainerRef.current = null
      return
    }
    scrollContainerRef.current = container

    // Seeded here because the listener is (re)attached at the start of every
    // streamed turn. `previousSnapshot` is where the view and the bottom were
    // as of this hook's last scroll or the last scroll event it saw, before
    // the content this commit added.
    const previousSnapshot = lastScrollRef.current
    const startSnapshot = takeScrollSnapshot(container)
    lastScrollRef.current = startSnapshot

    const handleScroll = () => {
      const last = lastScrollRef.current
      const now = takeScrollSnapshot(container)
      // Only an UPWARD move means the user scrolled away (wheel, touch, keys,
      // scrollbar drag, a jump to a citation), however small and even within
      // the bottom threshold: a citation's smooth scroll starts with a move of
      // a few px. Except for the part a shrinking range explains: the browser
      // moves the view up by itself by up to that much, clamping it to a
      // shorter range (the typing indicator went, the composer shrank after
      // sending) or scroll anchoring it when content above the view shrank.
      const movedUpBy = last.top - now.top
      const explainedBy = Math.min(Math.max(last.range - now.range, 0), Math.max(movedUpBy, 0))
      const unexplainedUpBy = movedUpBy - explainedBy
      if (unexplainedUpBy > SCROLL_UP_TOLERANCE_PX) {
        lastScrollRef.current = now
        isAutoScrollEnabledRef.current = false
        clearPendingScroll()
        return
      }
      // Within the tolerance, keep the unexplained part pending against the
      // old position so slow moves of a pixel or less add up; otherwise
      // measure the next move from here.
      lastScrollRef.current =
        unexplainedUpBy > 0 ? { top: last.top - explainedBy, range: now.range } : now
      // Re-arm at the bottom only after a move that was not upward: an upward
      // move within the tolerance (a gentle flick, one wheel notch at
      // browser zoom) has just been switched off by the wheel/touch listener
      // and must stay off. Uses the tight resume threshold; see its
      // definition.
      if (unexplainedUpBy <= 0 && isSnapshotAtBottom(now, SCROLL_RESUME_THRESHOLD_PX)) {
        isAutoScrollEnabledRef.current = true
      }
      // Any other move short of the bottom leaves following as it is and keeps
      // any queued pin. A downward one is usually this hook's own scroll: the
      // browser fires the scroll event for a pin a frame later, and by then
      // the typewriter has often grown the answer by a line, so the pin
      // "lands" a few px above the new bottom. Treating that as scroll-away
      // switched following off with nobody touching the page (#2994).
    }

    // Any user-initiated upward gesture should immediately disable auto-pin,
    // even if the cursor/touch didn't actually move the scrollTop yet (e.g.
    // during bursty streaming when `scrollHeight` grows faster than the
    // scroll handler can react). Without this, the `ResizeObserver` below
    // would keep snapping the container back to the bottom after every
    // chunk, making it feel like the user "can't scroll up".
    const disableAutoPinOnUpwardIntent = (deltaY: number) => {
      if (deltaY < 0) {
        isAutoScrollEnabledRef.current = false
        clearPendingScroll()
      }
    }
    const handleWheel = (event: WheelEvent) => {
      disableAutoPinOnUpwardIntent(event.deltaY)
    }
    let lastTouchY: number | null = null
    const handleTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches.item(0)?.clientY ?? null
    }
    const handleTouchMove = (event: TouchEvent) => {
      const y = event.touches.item(0)?.clientY
      if (y == null || lastTouchY == null) return
      // Swiping down (finger moves down on screen) scrolls content UP
      const deltaY = lastTouchY - y
      lastTouchY = y
      disableAutoPinOnUpwardIntent(deltaY)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === "ArrowUp" ||
        event.key === "PageUp" ||
        (event.key === "Home" && !event.ctrlKey && !event.metaKey)
      ) {
        disableAutoPinOnUpwardIntent(-1)
      }
    }

    // Start from where the view really is. A new turn has just been pinned to
    // the bottom by the messages effect, so it follows; a reader who is away
    // when a stream (re)starts (Deep Explorer toggling isStreaming mid-turn,
    // a second tab joining) stays put. The content this commit added can
    // already push the bottom away, and while streaming was paused no
    // listener saw a reader come back down, so being at the bottom as of the
    // previous snapshot counts too.
    isAutoScrollEnabledRef.current =
      isSnapshotAtBottom(startSnapshot, SCROLL_RESUME_THRESHOLD_PX) ||
      startSnapshot.top >= previousSnapshot.range - SCROLL_RESUME_THRESHOLD_PX

    container.addEventListener("scroll", handleScroll, { passive: true })
    container.addEventListener("wheel", handleWheel, { passive: true })
    container.addEventListener("touchstart", handleTouchStart, { passive: true })
    container.addEventListener("touchmove", handleTouchMove, { passive: true })
    container.addEventListener("keydown", handleKeyDown)
    return () => {
      container.removeEventListener("scroll", handleScroll)
      container.removeEventListener("wheel", handleWheel)
      container.removeEventListener("touchstart", handleTouchStart)
      container.removeEventListener("touchmove", handleTouchMove)
      container.removeEventListener("keydown", handleKeyDown)
      clearPendingScroll()
    }
  }, [chatState.isStreaming, chatMessageStreamEnd])

  useEffect(() => {
    // Immediate scroll when new streamed content arrives during streaming
    if (chatState.isStreaming && chatState.streamingAnswer) {
      const endElement = chatMessageStreamEnd.current
      if (!endElement) return

      const container = scrollContainerRef.current ?? resolveChatScrollContainer(endElement)
      if (!container) {
        scrollContainerRef.current = null
        return
      }
      scrollContainerRef.current = container

      const shouldAutoScroll = isAutoScrollEnabledRef.current

      if (!shouldAutoScroll) return

      if (pendingScrollTimeoutIdRef.current !== null) {
        window.clearTimeout(pendingScrollTimeoutIdRef.current)
      }

      // Use setTimeout to ensure DOM has updated with new content
      pendingScrollTimeoutIdRef.current = window.setTimeout(() => {
        pendingScrollTimeoutIdRef.current = null
        const stillShouldAutoScroll = isAutoScrollEnabledRef.current
        if (!stillShouldAutoScroll) return
        scrollContainerToBottom(container, lastScrollRef)
      }, 0)
    }
    // `streamingVersion` is included so Deep Explorer callers can force this
    // effect to fire when the tuple reference is preserved across batched
    // chunk updates (see UseChatScrollProps.streamingVersion docstring).
  }, [chatState.streamingAnswer, chatState.isStreaming, streamingVersion])

  // Keep pinned to bottom on content size increases.
  useEffect(() => {
    const endElement = chatMessageStreamEnd.current
    if (!endElement) return

    const container = scrollContainerRef.current ?? resolveChatScrollContainer(endElement)
    if (!container) {
      scrollContainerRef.current = null
      return
    }
    scrollContainerRef.current = container

    let lastHeight = container.scrollHeight

    const handleResize = () => {
      const { scrollTop, clientHeight, scrollHeight } = container
      const wasAtBottom = scrollTop + clientHeight >= lastHeight - SCROLL_STICKY_THRESHOLD_PX

      if (scrollHeight > lastHeight) {
        // Height increased; if we were near bottom AND auto-scroll wasn't
        // explicitly disabled by a user gesture, keep us pinned. Without the
        // `isAutoScrollEnabledRef` gate, bursty streaming will snap the user
        // back to the bottom on every chunk as long as they haven't scrolled
        // more than SCROLL_STICKY_THRESHOLD_PX away yet — which can be
        // practically impossible while content is growing rapidly.
        if (wasAtBottom && isAutoScrollEnabledRef.current) {
          scrollContainerToBottom(container, lastScrollRef)
        }
        lastHeight = scrollHeight
      } else {
        lastHeight = scrollHeight
      }
    }

    // Use ResizeObserver to detect layout changes that affect document height
    const observer = new ResizeObserver(handleResize)
    observer.observe(endElement.parentElement ?? container)

    return () => observer.disconnect()
  }, [chatMessageStreamEnd])
}
